import { Minimize2, Play } from "lucide-react";
import { CSSProperties, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { PageSection, PageSectionAction } from "nazare-ui/components/page-section";

import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../../scroll-shadow";
import { useSecondaryHeader } from "../../../../secondary-header-context";
import { SkeletonRow } from "../../../../skeleton-row";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useDocumentTitle } from "../../../../use-document-title";
import { useWorkflowDefinitions, type WorkflowDefinition } from "../../../../workflows-api";
import { WorkflowRunModal } from "../workflow-run-modal";
import styles from "./definitions.module.css";

export function WorkflowDefinitionsPage() {
  useDocumentTitle("Workflow Definitions");
  const [params, setParams] = useSearchParams();
  const runnableOnly = params.get("filter") === "runnable";
  const query = useWorkflowDefinitions();
  const [selectedDefinition, setSelectedDefinition] = useState<WorkflowDefinition | null>(null);
  const definitions = (query.data?.definitions ?? []).filter((item) => !runnableOnly || item.allowManual);
  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const filterRow = useMemo(() => (
    <div className={styles.filterRow}>
      <div className={styles.filterTabs} role="tablist" aria-label="Filter workflow definitions">
        <button type="button" role="tab" aria-selected={!runnableOnly} className={`${styles.filterTab} ${!runnableOnly ? styles.filterTabActive : ""}`} onClick={() => setParams({}, { replace: true })}>All definitions</button>
        <button type="button" role="tab" aria-selected={runnableOnly} className={`${styles.filterTab} ${runnableOnly ? styles.filterTabActive : ""}`} onClick={() => setParams({ filter: "runnable" }, { replace: true })}>Runnable only</button>
      </div>
    </div>
  ), [runnableOnly, setParams]);
  useSecondaryHeader(filterRow);

  return (
    <div className={styles.page}>
      <PageSection
        className={styles.card}
        style={{ viewTransitionName: "wf-card-definitions" } as CSSProperties}
        title="Workflow definitions"
        count={query.data ? definitions.length : undefined}
        actions={
          <PageSectionAction asChild aria-label="Minimise to dashboard" title="Minimise to dashboard">
            <Link to="/workflows" viewTransition><Minimize2 aria-hidden="true" /></Link>
          </PageSectionAction>
        }
      >
        <div className={styles.scrollShadowWrapper}>
          <div ref={tableWrapRef} className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th className={styles.th}>Name</th><th className={styles.th}>Description</th><th className={`${styles.th} ${styles.thActions}`}>Actions</th></tr></thead>
              <tbody>
                {query.isLoading ? Array.from({ length: 8 }, (_, index) => <SkeletonRow key={index} columns={3} />) : query.error ? (
                  <tr><td className={styles.td} colSpan={3}>Couldn’t load workflow definitions.</td></tr>
                ) : definitions.length === 0 ? (
                  <tr><td className={styles.td} colSpan={3}>No workflow definitions match this filter.</td></tr>
                ) : definitions.map((definition) => (
                  <tr key={definition.name} className={styles.row}>
                    <td className={styles.td}><div className={styles.name}>{definition.label}</div><div className={styles.id}>{definition.name}</div></td>
                    <td className={`${styles.td} ${styles.description}`}>{definition.description}</td>
                    <td className={`${styles.td} ${styles.actionsCell}`}>
                      {definition.allowManual ? (
                        <button type="button" className={styles.runButton} onClick={() => setSelectedDefinition(definition)}><Play className={styles.runIcon} aria-hidden="true" />Run</button>
                      ) : <span className={styles.noAction}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </PageSection>
      {selectedDefinition && <WorkflowRunModal definition={selectedDefinition} onClose={() => setSelectedDefinition(null)} />}
    </div>
  );
}
